// Copied from: https://github.com/FormidableLabs/victory/blob/main/packages/victory-core/src/victory-theme/material.tsx
// and modified to fit our theme.

import { VictoryThemeDefinition } from "victory";

// These colors are used by VictoryGroup to automatically assign unique colors
// to lines within the group. These colors are picked from Google charts.
export const chartColors: string[] = [
  "#3366CC",
  "#DC3912",
  "#FF9900",
  "#109618",
  "#990099",
  "#3B3EAC",
  "#0099C6",
  "#DD4477",
  "#66AA00",
  "#B82E2E",
  "#316395",
  "#994499",
  "#22AA99",
  "#AAAA11",
  "#6633CC",
  "#E67300",
  "#8B0707",
  "#329262",
  "#5574A6",
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#C9CBCF",
  "#4D5360",
  "#8E44AD",
  "#2ECC71",
  "#E74C3C",
  "#3498DB",
  "#F1C40F",
  "#1ABC9C",
  "#E67E22",
  "#95A5A6",
  "#34495E",
  "#D35400",
  "#2980B9",
  "#27AE60",
  "#FFB3BA",
  "#B3FFFF",
  "#1F77B4",
  "#FF7F0E",
  "#2CA02C",
  "#D62728",
  "#9467BD",
  "#8C564B",
  "#E377C2",
  "#7F7F7F",
  "#BCBD22",
  "#17BECF",
  "#1A5276",
  "#1D8348",
  "#922B21",
  "#76448A",
  "#A04000",
  "#212F3D",
  "#145A32",
  "#0E6251",
  "#1B4F72",
  "#4A235A",
  "#FFDFBA",
  "#FFFFBA",
  "#BAFFC9",
  "#BAE1FF",
  "#D5AAFF",
  "#FFB3F7",
  "#B3F2FF",
  "#C4FFB3",
  "#F2FFB3",
  "#FFE2B3",
  "#FFB3B3",
];

type ChartDrawColors = { color: string; id?: string };

export const drawColors: ChartDrawColors[] = chartColors.map((c) => ({
  color: c,
}));

// TODO: these might need to be changed when we have more chart types
const primary = "#003865";
const darkPrimary = "#000654";

const padding = 8;

export const brushHeight = 50;

export const brushPatternId = "brush_pattern";

export const brushPadding = {
  // Brush handle is cut off without this small horizontal padding
  left: 5,
  right: 5,
  // Avoid cutting of the brush background stroke
  top: 1,
  bottom: 1,
};

export const chartPadding = {
  top: 0,
  left: 50,
  right: 0,
  bottom: 50,
};

const baseProps = {
  width: 350,
  height: 350,
  padding: chartPadding,
};

const baseLabelStyles = {
  fontFamily: "'museo-sans'",
  fontSize: 12,
  letterSpacing: "normal",
  padding,
  fill: "#000",
  stroke: "transparent",
  strokeWidth: 0,
};

const centeredLabelStyles = Object.assign(
  { textAnchor: "middle" },
  baseLabelStyles,
);

const strokeLinecap = "round";
const strokeLinejoin = "round";

export const customTheme: VictoryThemeDefinition = {
  area: Object.assign(
    {
      style: {
        data: { fill: darkPrimary },
        labels: baseLabelStyles,
      },
    },
    baseProps,
  ),
  axis: Object.assign(
    {
      style: {
        axis: {
          fill: "transparent",
          stroke: "#000",
          strokeWidth: 1,
          strokeLinecap,
          strokeLinejoin,
        },
        axisLabel: Object.assign({}, centeredLabelStyles, {
          padding,
          stroke: "transparent",
        }),
        grid: {
          fill: "none",
          stroke: "rgba(0,0,0,0.1)",
          strokeDasharray: "10, 5",
          strokeLinecap,
          strokeLinejoin,
          pointerEvents: "painted",
        },
        ticks: {
          fill: "transparent",
          size: 5,
          stroke: "#000",
          strokeWidth: 1,
          strokeLinecap,
          strokeLinejoin,
        },
        tickLabels: Object.assign({}, baseLabelStyles, {
          fill: "#000",
        }),
      },
    },
    baseProps,
  ),
  polarDependentAxis: Object.assign({
    style: {
      ticks: {
        fill: "transparent",
        size: 1,
        stroke: "transparent",
      },
    },
  }),
  bar: Object.assign(
    {
      style: {
        data: {
          fill: primary,
          padding,
          strokeWidth: 0,
        },
        labels: baseLabelStyles,
      },
    },
    baseProps,
  ),
  boxplot: Object.assign(
    {
      style: {
        max: { padding, stroke: primary, strokeWidth: 1 },
        maxLabels: Object.assign({}, baseLabelStyles, { padding: 3 }),
        median: { padding, stroke: primary, strokeWidth: 1 },
        medianLabels: Object.assign({}, baseLabelStyles, { padding: 3 }),
        min: { padding, stroke: primary, strokeWidth: 1 },
        minLabels: Object.assign({}, baseLabelStyles, { padding: 3 }),
        q1: { padding, fill: primary },
        q1Labels: Object.assign({}, baseLabelStyles, { padding: 3 }),
        q3: { padding, fill: primary },
        q3Labels: Object.assign({}, baseLabelStyles, { padding: 3 }),
      },
      boxWidth: 20,
    },
    baseProps,
  ),
  candlestick: Object.assign(
    {
      style: {
        data: {
          stroke: primary,
        },
        labels: Object.assign({}, baseLabelStyles, { padding: 5 }),
      },
      candleColors: {
        positive: "#ffffff",
        negative: primary,
      },
    },
    baseProps,
  ),
  chart: baseProps,
  errorbar: Object.assign(
    {
      borderWidth: 8,
      style: {
        data: {
          fill: "transparent",
          opacity: 1,
          stroke: primary,
          strokeWidth: 2,
        },
        labels: baseLabelStyles,
      },
    },
    baseProps,
  ),
  group: Object.assign(
    {
      colorScale: chartColors,
    },
    baseProps,
  ),
  histogram: Object.assign(
    {
      style: {
        data: {
          fill: primary,
          stroke: darkPrimary,
          strokeWidth: 2,
        },
        labels: baseLabelStyles,
      },
    },
    baseProps,
  ),
  legend: {
    colorScale: chartColors,
    gutter: 10,
    orientation: "vertical",
    titleOrientation: "top",
    style: {
      data: {
        type: "circle",
      },
      labels: baseLabelStyles,
      title: Object.assign({}, baseLabelStyles, { padding: 5 }),
    },
  },
  line: Object.assign(
    {
      style: {
        data: {
          fill: "transparent",
          opacity: 1,
          stroke: primary,
          strokeWidth: 2,
        },
        labels: baseLabelStyles,
      },
    },
    baseProps,
  ),
  pie: Object.assign(
    {
      colorScale: chartColors,
      style: {
        data: {
          padding,
          stroke: "#ECEFF1",
          strokeWidth: 1,
        },
        labels: Object.assign({}, baseLabelStyles, { padding: 20 }),
      },
    },
    baseProps,
  ),
  scatter: Object.assign(
    {
      style: {
        data: {
          fill: primary,
          opacity: 1,
          stroke: "transparent",
          strokeWidth: 0,
        },
        labels: baseLabelStyles,
      },
    },
    baseProps,
  ),
  stack: Object.assign(
    {
      colorScale: chartColors,
    },
    baseProps,
  ),
  tooltip: {
    style: Object.assign({}, baseLabelStyles, {
      padding: 0,
      pointerEvents: "none",
    }),
    flyoutStyle: {
      stroke: darkPrimary,
      strokeWidth: 1,
      fill: "#f0f0f0",
      pointerEvents: "none",
    },
    flyoutPadding: 5,
    cornerRadius: 5,
    pointerLength: 10,
  },
  voronoi: Object.assign(
    {
      style: {
        data: {
          fill: "transparent",
          stroke: "transparent",
          strokeWidth: 0,
        },
        labels: Object.assign({}, baseLabelStyles, {
          padding: 5,
          pointerEvents: "none",
        }),
        flyout: {
          stroke: darkPrimary,
          strokeWidth: 1,
          fill: "#f0f0f0",
          pointerEvents: "none",
        },
      },
    },
    baseProps,
  ),
};
